/**
 * 来电提醒（防漏接）：振铃音 + 标签页标题闪烁 + 后台系统通知。
 * 振铃音用 WebAudio 合成国标回铃音（450Hz，1s 响 / 4s 停循环），无需音频资源文件。
 * 浏览器自动播放策略要求页面先有过用户交互，模块级 pointerdown 兜底 resume。
 *
 * 音量与开关取自 `utils/ringPrefs.ts`（界面设置 → 提醒），每次发声前读取，故改设置即时生效。
 * 「关闭」只停振铃循环，标题闪烁与系统通知照旧——那是页面不在前台时的兜底，属防漏接的一部分。
 * 提示音走本模块自己的 WebAudio 增益，不经通话音频链路，因此调它不会影响通话声音。
 */
import { maskPhone } from './format';
import { ringEnabled, ringGain } from './ringPrefs';

let ctx: AudioContext | null = null;
let ringing = false;
let cycleTimer: number | null = null;
let titleTimer: number | null = null;
/**
 * 标签页基础标题（当前页面名，由 PageContainer 随路由写入）：本模块是 `document.title` 的**唯一写者**——
 * 来电/风险提醒的闪烁都基于它切换，停止闪烁时也回到它，避免"恢复成进入闪烁前的旧标题"。
 */
let baseTitle = 'Lychee 智能客服管理台';
let notify: Notification | null = null;
// 通用标题闪烁（风险上报大卡片等复用）：独立 timer，不与来电提醒 titleTimer 冲突
let flashTimer: number | null = null;

function ensureCtx(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  } catch {
    return null;
  }
  return ctx;
}

// 自动播放策略兜底：用户任意点击后恢复音频上下文（坐席登录/操作后必触发）
if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', () => void ctx?.resume().catch(() => undefined));
}

/** 单次振铃：450Hz 正弦波，带音量包络防爆音；峰值取当前「提醒音量」档 */
function beepOnce(durationMs: number) {
  const ac = ensureCtx();
  if (!ac) return;
  const peak = ringGain();
  if (peak <= 0) return;
  try {
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = 'sine';
    osc.frequency.value = 450;
    osc.connect(gain);
    gain.connect(ac.destination);
    const t = ac.currentTime;
    const d = durationMs / 1000;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(peak, t + 0.02);
    gain.gain.setValueAtTime(peak, t + Math.max(0.02, d - 0.05));
    gain.gain.linearRampToValueAtTime(0, t + d);
    osc.start(t);
    osc.stop(t + d);
    osc.onended = () => {
      osc.disconnect();
      gain.disconnect();
    };
  } catch {
    /* 音频失败不阻断来电流程 */
  }
}

/** 登记当前页面的基础标题（PageContainer 随路由调用）；闪烁进行中不覆盖，闪烁结束后自然回到新标题 */
export function setBaseTitle(t: string) {
  baseTitle = t.trim() ? `${t.trim()}｜Lychee 智能客服` : 'Lychee 智能客服管理台';
  if (!ringing && flashTimer === null) document.title = baseTitle;
}

/** 开始来电提醒（幂等）：振铃音循环 + 标题闪烁 + 系统通知 */
export function startRingAlert(remoteNumber: string) {
  if (ringing) return;
  ringing = true;

  // 1) 振铃音：450Hz，1s 响 / 4s 停。
  // 「关闭」档只跳过这一段：标题闪烁与系统通知（下面 2、3）照旧，它们才是页面不在前台时的兜底。
  const ac = ringEnabled() ? ensureCtx() : null;
  if (ac) {
    void ac.resume().catch(() => undefined);
    const cycle = () => {
      if (!ringing) return;
      beepOnce(1000);
      cycleTimer = window.setTimeout(cycle, 5000);
    };
    cycle();
  }

  // 2) 标题闪烁（标签页在后台时可见）：基于 setBaseTitle 登记的页面标题切换
  const text = `【来电】${maskPhone(remoteNumber) || '未知号码'}`;
  let on = true;
  document.title = text;
  titleTimer = window.setInterval(() => {
    document.title = on ? baseTitle : text;
    on = !on;
  }, 1000);

  // 3) 系统通知（页面在后台时强提醒；未授权则静默跳过）
  try {
    if ('Notification' in window && Notification.permission === 'granted') {
      notify = new Notification('Lychee 智能客服 来电', {
        body: `来电号码：${maskPhone(remoteNumber) || '未知号码'}`,
        tag: 'bcc-incoming-call',
      });
      notify.onclick = () => window.focus();
    }
  } catch {
    /* 部分环境要求通知由 ServiceWorker 发出，忽略 */
  }
}

/** 通用告警提示音（高危通知弹窗等复用）：短促连响 cycles 次，无需音频资源。 */
export function playAlertSound(cycles = 2) {
  const ac = ensureCtx();
  if (!ac) return;
  void ac.resume().catch(() => undefined);
  let count = 0;
  const play = () => {
    if (count >= cycles) return;
    count += 1;
    beepOnce(300);
    window.setTimeout(play, 450);
  };
  play();
}

/** 通用标签页标题闪烁（风险上报大卡片等强提醒复用，直到 stopTitleFlash）：
 * 在闪烁文案与 setBaseTitle 登记的页面标题间 1s 切换；幂等（已在闪烁则更新文案不重启计时器）。
 * 与来电提醒 startRingAlert 的标题闪烁相互独立。 */
export function startTitleFlash(text: string) {
  if (flashTimer === null) {
    let on = true;
    document.title = text;
    flashTimer = window.setInterval(() => {
      document.title = on ? baseTitle : text;
      on = !on;
    }, 1000);
  } else {
    document.title = text;
  }
}

/** 停止通用标题闪烁（点「查看通话」处理上报后调用）：恢复页面标题、清除计时器 */
export function stopTitleFlash() {
  if (flashTimer !== null) {
    clearInterval(flashTimer);
    flashTimer = null;
  }
  document.title = baseTitle;
}

/** 停止来电提醒（接听/拒接/对方取消/注销） */
export function stopRingAlert() {
  ringing = false;
  if (cycleTimer !== null) {
    clearTimeout(cycleTimer);
    cycleTimer = null;
  }
  if (titleTimer !== null) {
    clearInterval(titleTimer);
    titleTimer = null;
  }
  document.title = baseTitle;
  try {
    notify?.close();
  } catch {
    /* noop */
  }
  notify = null;
}

/** 坐席注册成功后调用一次：预请求系统通知授权（被拒/忽略后不再打扰） */
export function requestNotifyPermission() {
  try {
    if ('Notification' in window && Notification.permission === 'default') {
      void Notification.requestPermission();
    }
  } catch {
    /* noop */
  }
}
